import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {OptimizationCardScratchpadState} from '../../scratchpad/optimizationCardScratchpad.types';
import getOrCreateOptimizationCard from '../../utils/optimization/getOrCreateOptimizationCard';
import setOptimizeByTool from './setOptimizeByTool';
import setOptimizeAcrossTool from './setOptimizeAcrossTool';
import addOptimizationOutputTool from './addOptimizationOutputTool';
import updateOptimizationOutputTool from './updateOptimizationOutputTool';
import setOptimizationGoalTool from './setOptimizationGoalTool';
import getOptimizeByOptionsTool from './getOptimizeByOptionsTool';
import setAttributionSignalsTool from './setAttributionSignalsTool';
import getAttributionSignalsTool from './getAttributionSignalsTool';

const cardSpecificInstructions = `### What is an Optimization card
An Optimization card automatically tests multiple creative variants and drops underperformers over time based on a performance metric.

### Optimization card rules
- Optimization cards do NOT have fallback outputs.
- Must have at least 2 outputs at all times.
- Attribution signals are only relevant for Conversions and ClickThroughConversions metrics. For ClickThroughConversions, only Click-type attribution signals are available.

### Property Dependencies
- The available optimization metrics depend on the campaign type and must be discovered (getOptimizeByOptions) before setting the metric.
- The optimization goal (number of variants to keep) must not exceed the number of outputs, so outputs must exist before the goal is set.
- Attribution signals are only available when the metric is Conversions or ClickThroughConversions — the metric must be set before configuring attribution. Available attribution signals must be discovered (getAttributionSignals) before they can be set.
- Optimize across (Campaign/Placement/AdSet), outputs, and card name/description have no dependencies on each other.`;

export default function addOrUpdateOptimizationCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, OptimizationCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateOptimizationCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateOptimizationCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.GENERAL_OPTIMIZATION]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.GENERAL_OPTIMIZATION,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),

        // Optimization tools
        getOptimizeByOptionsTool(store),
        setOptimizeByTool(),
        setOptimizeAcrossTool(),
        addOptimizationOutputTool(),
        updateOptimizationOutputTool(),
        setOptimizationGoalTool(),
        getAttributionSignalsTool(),
        setAttributionSignalsTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateOptimizationCard,
    description:
      'Add or update an Optimization card. Card automatically optimizes output distribution based on performance metrics (Clicks, Conversions, etc.).',
    displayName: 'Working on optimization card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
